import { isValidElement, type ReactNode } from 'react';
import { formatBudgetCompact } from '../lib/format';
import { HISPANIC_EDITION } from '../lib/hispanic-edition';

/**
 * Spain: the placeholder is a SHAPE, not text. A blurred «88,8 %» or «#888888»
 * is still a string in the page — in the prerendered HTML, in the DOM Google
 * renders — and sat next to real company names (QUIRÓN PREVENCIÓN, ACCIONA), so
 * a search engine could quote it in an open lot's snippet (global review 21.10,
 * С5). A bar the width of the text it stands for carries no character at all;
 * blurred, it reads the same. UZ and KZ keep the all-8 text below.
 */
const SHAPE_PLACEHOLDERS = HISPANIC_EDITION;

/** Characters of plain text inside `node` — strings and numbers, through arrays
 *  and fragments. The width of the bar that stands in for it. */
export function placeholderTextLength(node: ReactNode): number {
  if (node == null || typeof node === 'boolean') return 0;
  if (typeof node === 'string' || typeof node === 'number') return String(node).length;
  if (Array.isArray(node)) return node.reduce<number>((n, c) => n + placeholderTextLength(c), 0);
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return placeholderTextLength(node.props.children);
  }
  return 0;
}

/**
 * A value slot on a LOCKED Pro card: the real card's typography, a blurred
 * placeholder instead of the number.
 *
 * Three rules, and each exists because of a way this could mislead:
 *
 * 1. **The placeholder is obviously fake once un-blurred.** Every digit is an
 *    `8` — «88.8%», «88 млн», «8 из 88 контрактов». A visitor who removes the
 *    blur in devtools (and someone always does) must land on nonsense, not on a
 *    number they could mistake for this tender's real one. The server never
 *    sends the real values at all; this is the second line, not the first.
 * 2. **It is invisible to assistive tech and to the cursor** — `aria-hidden`,
 *    `pointer-events: none`, `user-select: none` — so nothing selects or reads
 *    out a fabricated figure. The card states «Доступно на Pro» once instead
 *    (`AnalyticsTeaser`).
 * 3. **It is deterministic.** No randomness anywhere, so SSR and hydration
 *    produce byte-identical markup and the prerendered page does not flicker.
 *
 * `data-teaser-placeholder` is a marker only (no value in it) — it is what the
 * leak test walks to assert every placeholder is all-8s and aria-hidden.
 */
export function BlurredValue({
  children,
  className,
  blurPx = 4,
}: {
  children: ReactNode;
  className?: string;
  /** Blur radius. 4 px everywhere except inside a pill, where the halo is wider
   *  than the pill's 8 px padding and reads as a smudged border even with the
   *  pill clipping it. */
  blurPx?: number;
}) {
  if (SHAPE_PLACEHOLDERS) {
    // ~0.55 em a character (the average glyph of the UI face), never wider than
    // its container; the landing's locked lot card uses the same grey bar.
    const chars = Math.max(2, placeholderTextLength(children));
    return (
      <span
        aria-hidden
        data-teaser-placeholder=""
        data-nosnippet=""
        className={`pointer-events-none inline-block h-[0.8em] max-w-full select-none rounded-[4px] bg-gradient-to-r from-[#C9C8C2] to-[#E4E3DE] align-middle${className ? ` ${className}` : ''}`}
        style={{ width: `${(chars * 0.55).toFixed(2)}em`, filter: `blur(${blurPx}px)` }}
      />
    );
  }
  return (
    <span
      aria-hidden
      data-teaser-placeholder=""
      className={`pointer-events-none select-none${className ? ` ${className}` : ''}`}
      style={{ filter: `blur(${blurPx}px)` }}
    >
      {children}
    </span>
  );
}

/** The one digit any placeholder may contain — see rule 1 above. */
export const PLACEHOLDER_DIGIT = '8';

/** A share, in the real cards' style (`formatPct`): «88.8%», «88,8 %» on Spain. */
export const PLACEHOLDER_PCT = HISPANIC_EDITION ? '88,8\u00A0%' : '88.8%';

/** A whole count (participants, contracts). */
export const PLACEHOLDER_COUNT = '88';

/** A Herfindahl index — four digits, like the real pill. */
export const PLACEHOLDER_HHI = '8888';

/** A procedure / lot number, as the re-listing row would show one. */
export const PLACEHOLDER_ID = '#888888';

/** A compact amount through the REAL formatter, so the unit word and the
 *  locale's separator are whatever the unlocked card would print («88 млн» /
 *  «88 mln»). `magnitude` picks the order of size; both inputs are all-8s. */
export function placeholderAmount(locale: string, magnitude: 'small' | 'large' = 'large'): string {
  return formatBudgetCompact(magnitude === 'large' ? '88000000' : '8000000', locale) ?? '88';
}
